/* eslint-disable max-len */
import {
  BillingAcceptanceAdvancedQuery,
  BillingAcceptanceAdvancedQueryVariables,
  BillingAcceptanceAdvancedDocument,
  AcceptanceAdvanceStatus,
} from '@shared/api/graphql/generated';

import BrowserUtils from '@zvuk-b2b/react-uikit/toolkit/BrowserUtils';

import { acceptanceAdvancedStatusUpdated, stopAcceptanceAdvancedProcess } from './slice';
import { AcceptanceAdvancedStopPollingArgs, PollAcceptanceAdvancedStatusArgs } from './types';
import { TEXTS } from './texts';
import { TEXTS as GLOBAL_POLLING_TEXTS } from '../globalPollingSlice/texts';
import {
  CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS,
  DEFAULT_MAX_ATTEMPTS,
  DEFAULT_POLLING_INTERVAL,
  DEFAULT_STOP_POLLING_TIMEOUT,
} from './consts';
import { startGlobalPolling, stopGlobalPolling } from '../globalPollingSlice';
import { ActionPollingConfig } from '../globalPollingSlice/types';

const stopPollingByReason = ({
  acceptanceAdvancedId,
  dispatch,
  reason,
}: Omit<AcceptanceAdvancedStopPollingArgs, 'isTabClosed' | 'acceptanceAdvancedTab'>) => {
  dispatch(stopAcceptanceAdvancedProcess({ acceptanceAdvancedId, clearData: true }));
  dispatch(stopGlobalPolling({ id: acceptanceAdvancedId, reason }));
};

const stopPullingWithCloseTab = ({
  acceptanceAdvancedId,
  dispatch,
  isTabClosed,
  reason,
}: AcceptanceAdvancedStopPollingArgs) => {
  if (!isTabClosed) {
    stopPollingByReason({ acceptanceAdvancedId, dispatch, reason });
  }
};

export const pollAcceptanceAdvancedStatus = async ({
  acceptanceAdvancedId,
  acceptanceAdvancedTab,
  companyId,
  dispatch,
  signal,
}: PollAcceptanceAdvancedStatusArgs): Promise<void> => {
  const checkTabStatus = (tab: Window | null) => {
    if (tab?.closed) {
      console.log(`📭 Tab closed for ID: ${acceptanceAdvancedId}`);

      stopPollingByReason({
        acceptanceAdvancedId,
        dispatch,
        reason: TEXTS.ACCEPTANCE_ADVANCED_TAB_CLOSED,
      });

      return true;
    }

    return false;
  };

  if (checkTabStatus(acceptanceAdvancedTab)) return;

  try {
    const actionConfig: ActionPollingConfig<BillingAcceptanceAdvancedQuery, BillingAcceptanceAdvancedQueryVariables> = {
      query: BillingAcceptanceAdvancedDocument,
      variables: { companyId },
      isComplete: (data) => {
        const status = data?.acceptanceAdvancedQuery?.status ?? null;

        return status !== AcceptanceAdvanceStatus.Pending;
      },
      onComplete: (data) => {
        const status = data?.acceptanceAdvancedQuery?.status ?? null;
        console.log(`🎉 Global Polling COMPLETE! Final status: ${status} for ID: ${acceptanceAdvancedId}`);

        setTimeout(() => {
          dispatch(stopAcceptanceAdvancedProcess({ acceptanceAdvancedId, clearData: false }));
        }, 100);
      },
      onError: (error) => {
        if (checkTabStatus(acceptanceAdvancedTab)) return;

        console.error(`❌ Global Polling error for ID: ${acceptanceAdvancedId}:`, error);

        if (error.message === GLOBAL_POLLING_TEXTS.POLLING_TIMEOUT) {
          stopPullingWithCloseTab({
            acceptanceAdvancedId,
            acceptanceAdvancedTab,
            dispatch,
            isTabClosed: acceptanceAdvancedTab.closed,
            reason: TEXTS.ACCEPTANCE_ADVANCED_TOO_LONG,
          });
        } else if (error.message === GLOBAL_POLLING_TEXTS.POLLING_TOO_MANY_ATTEMPTS) {
          stopPullingWithCloseTab({
            acceptanceAdvancedId,
            acceptanceAdvancedTab,
            dispatch,
            isTabClosed: acceptanceAdvancedTab.closed,
            reason: TEXTS.ACCEPTANCE_ADVANCED_TOO_MANY_ATTEMPTS,
          });
        } else {
          dispatch(
            acceptanceAdvancedStatusUpdated({
              acceptanceAdvancedId,
              error: error.message,
            })
          );
        }
      },
      pollingInterval: DEFAULT_POLLING_INTERVAL,
      maxAttempts: DEFAULT_MAX_ATTEMPTS,
      timeout: DEFAULT_STOP_POLLING_TIMEOUT,
      context: { fetchOptions: { signal } },
    };

    dispatch(
      startGlobalPolling({
        id: acceptanceAdvancedId,
        config: actionConfig,
      })
    );
  } catch (error) {
    if (!signal.aborted) {
      console.error(`🚨 Failed to start global polling for ID: ${acceptanceAdvancedId}:`, error);

      stopPollingByReason({
        acceptanceAdvancedId,
        dispatch,
        reason: 'Failed to start global polling process',
      });
    }
  }
};

export class LinkExpirationManager {
  private timers = new Map<string, NodeJS.Timeout>();

  scheduleExpiration(acceptanceAdvancedId: string, expiresAt: Date) {
    this.cancelExpiration(acceptanceAdvancedId);

    const expiresAtUTC = expiresAt.getTime();
    const nowUTC = Date.now();
    const timeToExpire = expiresAtUTC - nowUTC;

    const timer = setTimeout(
      () => {
        this.handleExpiration(acceptanceAdvancedId);
      },
      Math.max(timeToExpire, 0)
    );

    this.timers.set(acceptanceAdvancedId, timer);
  }

  private handleExpiration(acceptanceAdvancedId: string) {
    const context = CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.get(acceptanceAdvancedId);

    if (!context) return;

    if (context.acceptanceAdvancedTab && !context.acceptanceAdvancedTab.closed) {
      context.acceptanceAdvancedTab.close();
    }

    context.abortController.abort();
    CURRENT_ACCEPTANCE_ADVANCED_CONTEXTS.delete(acceptanceAdvancedId);
    this.timers.delete(acceptanceAdvancedId);
  }

  cancelExpiration(acceptanceAdvancedId: string) {
    const timer = this.timers.get(acceptanceAdvancedId);

    if (timer) {
      clearTimeout(timer);
      this.timers.delete(acceptanceAdvancedId);

      return true;
    }

    return false;
  }
}

const SIGNING_PRELOAD_HTML = `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="utf-8">
<title>Загрузка...</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:SBSansText,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:hsl(228,20%,10%)}
  .wrap{text-align:center;padding:32px}
  .spinner{width:140px;height:140px;border:calc(0.1em + 2px) solid transparent;border-left-color:#fff;border-radius:50%;font-size:1px;animation:spin 1.1s linear infinite;margin:0 auto 32px}
  @keyframes spin{to{transform:rotate(360deg)}}
  p{color:#fff;font-size:16px;line-height:1.5;font-weight:500}
</style>
</head>
<body>
<div class="wrap">
  <div class="spinner"></div>
  <p>Подождите, генерируется<br>ссылка на подписание</p>
</div>
</body>
</html>`;

export const openSigningPreloadTab = (): Window | null => {
  if (BrowserUtils.isIOS || BrowserUtils.isSafariDesktop) {
    return null;
  }

  const blob = new Blob([SIGNING_PRELOAD_HTML], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank');

  setTimeout(() => URL.revokeObjectURL(url), 10000);

  return tab;
};

export const getMoscowTimeToday = (hour: number, date: Date = new Date()): Date => {
  const utcHour = hour - 3;

  const utcDate = new Date(date.toISOString());
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth();
  const day = utcDate.getUTCDate();

  const result = new Date(Date.UTC(year, month, day, utcHour, 0, 0, 0));

  const now = new Date();
  if (result.getTime() <= now.getTime()) {
    result.setUTCDate(result.getUTCDate() + 1);
  }

  return result;
};
